import React, {useEffect, useMemo, useRef, useState} from "react";
import moment from "moment";

// Stile comune
import styles from "./CommonReportsPage.module.scss";

// Se hai un file "ReportStyles.module.css" con classi come reportMainBox, importalo pure
// Se preferisci tenerle direttamente in CommonReportsPage.module.scss, va bene uguale.
// Esempio:
// import reportStyles from "@/commons/styles/ReportStyles.module.css";
import Post from "@/commons/objects/post/Post";
import {getUserHead} from "@/commons/utils/others/Users";
import Redirector from "@/commons/utils/Redirects";
import {useUser} from "@/contexts/UserContext";
import ApiInterface from "@/commons/api/ApiInterface";
import CustomizedTooltip from "@/commons/utils/toolpit/CustomizedTooltip";
import FloatingInput from "@/commons/components/fields/floatingInput/FloatingInput";
import DropdownField from "@/commons/components/fields/dropdownField/DropdownField";
import CustomSelect from "@/commons/components/fields/customSelect/CustomSelect";
import CustomButton from "@/commons/components/buttons/customButton/CustomButton";

/**
 * colorazione status, restituisce stringa HTML
 */
function coloredStatus(status) {
    switch ((status || "").toUpperCase()) {
        case "HIDED":
            return `<span style='color:goldenrod; font-weight:bold'>NASCOSTO</span>`;
        case "CLOSED":
            return `<span style='color:darkred; font-weight:bold'>CHIUSO</span>`;
        case "OPEN":
            return `<span style='color:green; font-weight:bold'>APERTO</span>`;
        default:
            return `<span style='color:goldenrod; font-weight:bold'>${(status || "none").toUpperCase()}</span>`;
    }
}

/**
 * Mostra un singolo report. Carica avatar dal getMinecraftHead e
 * chiama onClick(reportId, event) al click.
 *
 * NOTA: Se usi un file "reportStyles" con .reportMainBox, .reportImage, ecc.,
 * sostituisci “styles.xxx” con “reportStyles.xxx” a piacere.
 */
function ReportItem({report, onClick, adminInfos}) {
    const {userLoadingComplete} = useUser();
    const [avatar, setAvatar] = useState(null);
    const [userComment, setUserComment] = useState(null); // nuovo stato
    const [staffComment, setStaffComment] = useState(null); // nuovo stato
    const [waitingResponse, setWaitingResponse] = useState(null)
    const [staffFirstResponse, setStaffFirstResponse] = useState(null)

    const [reportBoxClass, setReportBoxClass] = useState(styles.ReportBox)

    async function loadBoxClasses() {
        const localDateTime = report.time;

        //calculate post open days
        const daysOpen = moment().diff(moment(localDateTime), 'days');
        let status = report.status;

        let reportClass = styles.ReportBox;
        let minDays = 5;

        if (status.toLowerCase() === "open" && adminInfos) {
            if (daysOpen >= minDays && daysOpen < 10) {
                // aggiungi classe a reportClass per giorni tra 5 e 10
                reportClass += ` ${styles.ReportWarning}`;
            }
            if (daysOpen >= 10 && daysOpen < 15) {
                // aggiungi classe a reportClass per giorni tra 10 e 15
                reportClass += ` ${styles.ReportSecondWarning}`;
            }
            if (daysOpen >= 15) {
                // aggiungi classe a reportClass per giorni >= 15
                reportClass += ` ${styles.ReportThirdWarning}`;
            }
            if (daysOpen >= 30) {
                // aggiungi classe a reportClass per giorni >= 15
                reportClass += ` ${styles.ReportFinalWarning}`;
            }

            if (staffFirstResponse === null && waitingResponse === null) {
                setReportBoxClass(reportClass);
                return;
            }

        }

        setReportBoxClass(reportClass);
    }

    //load avatar
    useEffect(() => {
        if (!userLoadingComplete) return;
        (async () => {
            async function loadMinecraftData() {
                const userName = report.user_id.username;
                const minecraftUID = report.user_id.minecraftUid;
                const mcAvatar = await getUserHead(minecraftUID, userName);
                setAvatar(mcAvatar);
            }

            async function loadCommentsData() {
                let staffComment = await ApiInterface.getLastStaffComment(report.id);
                let userComment = await ApiInterface.getLastUserComment(report.id);

                setStaffComment(staffComment);
                setUserComment(userComment);

                if (staffComment) {
                    setStaffFirstResponse(true);
                    if (userComment) {
                        let staffTime = moment(staffComment.time).valueOf();
                        let userTime = moment(userComment.time).valueOf();
                        if (staffTime > userTime) {
                            setWaitingResponse(false);
                        } else {
                            setWaitingResponse(true);
                        }
                    }
                } else {
                    setStaffFirstResponse(false);
                }
            }


            loadMinecraftData();
            loadCommentsData().then(loadBoxClasses);
        })();
    }, [userLoadingComplete]);

    useEffect(() => {
        loadBoxClasses()
    }, [waitingResponse, staffFirstResponse])

    const localDateTime = report.time;
    const parsedDate = moment(localDateTime).valueOf();
    const timeStr = moment(parsedDate).format("DD.MM.YYYY HH:mm:ss");

    //calculate post open days
    const daysOpen = moment().diff(moment(localDateTime), 'days');
    let status = report.status;

    let minDays = 5;

    function handleClick(event) {
        if (onClick) onClick(report.id, event);
    }

    //console.log(comment);

    const calculateTimeSince = (time) => {
        return formatTime(time);
    };

    const formatTime = (time) => {
        const duration = moment.duration(moment().diff(moment(time))); // Calcola la differenza temporale

        // Calcola ogni componente del tempo
        const years = Math.floor(duration.asYears());
        const months = Math.floor(duration.asMonths() % 12);
        const weeks = Math.floor(duration.asWeeks() % 4);
        const days = Math.floor(duration.asDays() % 7);
        const hours = duration.hours();
        const minutes = duration.minutes();

        // Chiama direttamente formatDuration per ottenere una stringa leggibile
        return formatDuration(years, months, weeks, days, hours, minutes);
    };

    function formatDuration(years, months, weeks, days, hours, minutes) {
        const yearsText = years > 0 ? `${years} ann${years > 1 ? "i" : "o"}` : "";
        const monthsText = months > 0 ? `${months} mes${months > 1 ? "i" : "e"}` : "";
        const weekText = weeks > 0 ? `${weeks} settiman${weeks > 1 ? "e" : "a"}` : "";
        const dayText = days > 0 ? `${days} giorn${days > 1 ? "i" : "o"}` : "";
        const hourText = hours > 0 ? `${hours} or${hours > 1 ? "e" : "a"}` : "";
        const minuteText = minutes > 0 ? `${minutes} minut${minutes > 1 ? "i" : "o"}` : "";

        // Crea un array con i valori non vuoti e li concatena con le virgole
        const parts = [yearsText, monthsText, weekText, dayText, hourText, minuteText].filter(part => part);
        return parts.join(", ").replace(/, ([^,]*)$/, " e $1"); // Sostituisce l'ultima virgola con "e"
    }

    const reportInfo = (showDiv = true) => {
        switch (status.toLowerCase()) {
            case "open": {
                if (report === undefined || report === null) return (<></>)
                // Converte report.time in un oggetto Moment
                const reportTime = moment(report.time);
                if (!reportTime.isValid()) {
                    // Gestisci il caso in cui la data non sia valida
                    return (
                        <div className={showDiv ? styles.reportActivityInfo : ""}>
                                        <span style={{fontWeight: "bold", color: "darkred"}}>
                                            Data non valida
                                        </span>
                        </div>
                    );
                }
                // Prepara il messaggio della durata
                const durationMessage = formatTime(reportTime);
                status = report.status;

                // Restituisce il messaggio "Aperto da ..."
                return (
                    <div className={showDiv ? styles.reportActivityInfo : ""}>
                                    <span style={{fontWeight: "bold", color: "lime"}}>
                                        {!staffFirstResponse && adminInfos && (
                                            <span style={{fontWeight: "bold", color: "red"}}>
                                            <>MAI RISPOSTO<br/><br/></>
                                            </span>
                                        )}
                                        Aperto da {durationMessage}
                                    </span>
                    </div>
                );
                break;
            }
            case "closed":
            default: {
                let text = "Chiuso da $1";
                let time;
                if ((userComment === undefined || userComment === null) && (staffComment === undefined || staffComment === null)) {
                    time = report.time;
                    text = "Aperto $1 fa"
                } else {
                    const isUserRecent = userComment && (!staffComment || moment(userComment.time).isAfter(staffComment.time));

                    time = isUserRecent ? (userComment.time) : (staffComment.time);
                }
                // Mostra l'avviso in base a chi ha il commento più recente
                const durationMessage = formatTime(time);

                text = text.replaceAll("$1", durationMessage);

                return (
                    <div className={showDiv ? styles.reportActivityInfo : ""}>
                                    <span style={{fontWeight: "bold", color: "red"}}>
                                        {text}
                                    </span>
                    </div>
                );
            }

        }
    }


    return (
        <div className={reportBoxClass} onClick={handleClick}>
            <div className={styles.reportRow}>
                {/* user/avatar */}
                <div className={styles.reportUsername} id="report_username">
                    {avatar && (
                        <img className={styles.reportImage} src={avatar} alt="avatar"/>
                    )}
                    <span>{report.user_id.username}</span>
                </div>
                {/* data/ora */}
                <div className={styles.reportTime}>
                    <span>{timeStr}</span>
                </div>
            </div>
            <div className={styles.reportRow}>
                {/* titolo */}
                <div className={styles.reportTitle} id="report_title">
                    <span>{report.title}</span>
                </div>
            </div>
            <div className={styles.reportRow}>
                {/* categoria */}
                <div className={styles.reportCategory} id="report_category">
                    <span>{report.category_id.name}</span>
                </div>
                {/* status */}
                <div className={styles.reportStatus}>
                    <span dangerouslySetInnerHTML={{__html: coloredStatus(report.status)}}/>
                    {adminInfos && status.toLowerCase() === "open" && daysOpen >= minDays && (
                        <span className={styles.boldText}>
                            {`Da ${daysOpen} giorni!`}
                        </span>
                    )}
                </div>
                <div className={styles.reportEmpty} id="emptyContainer">
                </div>
            </div>

            {/* Mostra le date dei commenti */}
            {(staffComment || userComment) && (
                <div className={styles.reportRow}>
                    <div className={styles.reportCommentsInfo}>
                        {staffComment && (
                            <CustomizedTooltip
                                tooltipContent={`Tempo trascorso: <span style='color: #ce9a17'>${calculateTimeSince(staffComment.time)}</span>`}>
                                <span> Risposta dello staff:{" "}
                                    <span
                                        style={{
                                            color: !userComment || moment(staffComment.time).isAfter(userComment.time) ? "lime" : "red",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        {moment(staffComment.time).format("DD/MM/YYYY HH:mm")}
                                    </span>
                                </span>
                            </CustomizedTooltip>
                        )}
                        {!staffComment && (
                            <span> Risposta dello staff:{" "}
                                <span style={{color: "red", fontWeight: "bold"}}>Nessuna risposta</span>
                            </span>
                        )}
                        {userComment && (
                            <CustomizedTooltip
                                tooltipContent={`Tempo trascorso: <span style='color: #ce9a17'>${calculateTimeSince(userComment.time)}</span>`}>
                                <span
                                > Risposta dell'utente:{" "}
                                    <span
                                        style={{
                                            color: !staffComment || moment(userComment.time).isAfter(staffComment.time) ? "lime" : "red",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        {moment(userComment.time).format("DD/MM/YYYY HH:mm")}
                                    </span>
                                </span>
                            </CustomizedTooltip>
                        )}
                        {!userComment && (
                            <span> Risposta dell'utente:{" "}
                                <span style={{color: "red", fontWeight: "bold"}}>Nessun messaggio</span>
                            </span>
                        )}
                    </div>
                </div>
            )}
            {!(staffComment || userComment) && (
                <div className={styles.reportRow}>
                    <div className={styles.reportCommentsInfo}>
                        <br/>
                        <span> Risposta dello staff:{" "}
                            <span style={{color: "red", fontWeight: "bold"}}>Nessuna risposta</span>
                        </span>

                        <span> Risposta dell'utente:{" "}
                            <span style={{color: "red", fontWeight: "bold"}}>Nessun messaggio</span>
                        </span>
                    </div>
                </div>
            )}

            {adminInfos && (
                <>
                    {/* Seconda sezione con condizioni sulle date */}
                    {status.toLowerCase() === "open" && (staffComment || userComment) ? (
                        <div className={styles.reportActivityInfo}>
                            {(() => {
                                // Trova il commento più recente
                                const isUserRecent = userComment && (!staffComment || moment(userComment.time).isAfter(staffComment.time));

                                const timeSinceLastComment = isUserRecent ? (userComment.time) : (staffComment.time);
                                // Mostra l'avviso in base a chi ha il commento più recente
                                const durationMessage = formatTime(timeSinceLastComment);

                                if (isUserRecent) {
                                    return (
                                        <CustomizedTooltip tooltipContent={(reportInfo(false))}>
                                            <span style={{color: "red", fontWeight: "bold"}}>
                                                {!staffFirstResponse && (
                                                    <>MAI RISPOSTO<br/><br/></>
                                                )}
                                                In attesa da {durationMessage}
                                            </span>
                                        </CustomizedTooltip>
                                    );
                                } else {
                                    return (
                                        <CustomizedTooltip tooltipContent={(reportInfo(false))}>
                                            <span style={{color: "darkgoldenrod", fontWeight: "bold"}}>
                                                Inattivo da {durationMessage}
                                            </span>
                                        </CustomizedTooltip>
                                    );
                                }
                            })()}
                        </div>
                    ) : (
                        // Caso in cui né lo staff né l'utente abbiano lasciato commenti
                        <>
                            {(reportInfo())}
                        </>
                    )}
                </>
            )}
            {!adminInfos && (reportInfo())}
        </div>
    );
}

/**
 * CommonReportsPage:
 *  - Filtri open/closed
 *  - Ricerca testuale
 *  - Paginazione
 *  - Caricamento asincrono (fetchReports)
 *  - Opzionale: pulsante "Crea nuovo Report"
 *
 * Props:
 *  - pageTitle
 *  - fetchReports(filter, page, itemsPerPage) => Promise di {content:[], totalElements}
 *  - onReportClick(reportId, event)
 *  - newReportButtonUrl (string) => se presente, appare pulsante "Crea nuovo Report"
 */
function CommonReportsPage({
                               pageTitle = "Reports",
                               fetchReports,
                               onReportClick,
                               adminInfos: adminInfos = false,
                               newReportButtonUrl = null,
                           }) {
    // Filtri open/closed
    const [isOpenFilter, setIsOpenFilter] = useState(true);
    const [isClosedFilter, setIsClosedFilter] = useState(false);
    const [isAllFilter, setIsAllFilter] = useState(false);

    // Ricerca
    const [searchTerm, setSearchTerm] = useState("");
    const prevSearchLen = useRef(0);

    // Paginazione
    const [page, setPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(15);
    const [maxPage, setMaxPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    // Stato array report
    const [reports, setReports] = useState([]);
    // Loading => blocca i filtri mentre carica
    const [isLoading, setIsLoading] = useState(false);
    const [focused, setFocused] = useState(false);

    function getCurrentFilter() {
        if (isOpenFilter && !isClosedFilter) return "open";
        if (!isOpenFilter && isClosedFilter) return "closed";
        return "all";
    }

    // Carica i report
    async function loadReports() {
        setIsLoading(true);
        try {
            const filter = getCurrentFilter();
            const json = await fetchReports(filter, page, itemsPerPage);
            if (json) {
                console.log(json);
                setTotalItems(json.totalElements);
                const newMaxPage = json.totalPages;
                setMaxPage(newMaxPage);

                const newPosts = json.postList.map((p) => new Post(p));
                setReports(newPosts);

                if (page >= newMaxPage && newMaxPage > 0) {
                    setPage(newMaxPage - 1);
                }
            }
        } catch (err) {
            console.error("Errore loadReports:", err);
        } finally {
            setIsLoading(false);
        }
    }

    // Ricarica quando cambiano page/itemsPerPage/filtri
    useEffect(() => {
        loadReports();
    }, [page, itemsPerPage, isOpenFilter, isClosedFilter]);

    // Logica "ricerca => itemsPerPage = totalItems"
    useEffect(() => {
        const oldLen = prevSearchLen.current;
        const newLen = searchTerm.length;

        if (oldLen === 0 && newLen > 0) {
            if (itemsPerPage < totalItems) setItemsPerPage(totalItems);
        } else if (newLen === 0 && oldLen > 0) {
            if (itemsPerPage >= totalItems) setItemsPerPage(15);
        }
        prevSearchLen.current = newLen;
    }, [searchTerm, itemsPerPage, totalItems]);

    // Filtraggio locale
    const filteredReports = useMemo(() => {
        if (!searchTerm) return reports;
        const up = searchTerm.toUpperCase();
        return reports.filter((rep) => {
            const t = rep.title?.toUpperCase() || "";
            const c = rep.category_id?.name?.toUpperCase() || "";
            const u = rep.user_id?.username?.toUpperCase() || "";
            return t.includes(up) || c.includes(up) || u.includes(up);
        });
    }, [searchTerm, reports]);

    // "X-Y/totalItems"
    const itemsString = (() => {
        if (totalItems === 0) return "0";
        const start = page * itemsPerPage + 1;
        const end = page * itemsPerPage + reports.length;
        return `${start}-${end} di ${totalItems}`;
    })();

    function scrollToTop(force = true) {
        setTimeout(() => {
            window.scrollTo({top: 0, behavior: 'smooth'});
        }, 0);
    }

    // Gestione pagina
    function handlePageChange(newPage) {
        if (isLoading) return;
        scrollToTop()
        if (newPage < 0) setPage(0);
        else if (newPage >= maxPage) setPage(maxPage - 1);
        else setPage(newPage);
    }

    // Gestione itemsPerPage
    function handleItemsPerPageChange(value) {
        if (isLoading) return;
        setItemsPerPage(parseInt(value, 10));
        setPage(0);
    }

    // Switch filtri open/closed
    function switchFilter(input) {
        if (isLoading) return;

        if (input === "open") {
            const nextOpen = !isOpenFilter;
            setIsOpenFilter(nextOpen);

            if (nextOpen) {
                setIsClosedFilter(false);
                setIsAllFilter(false);
            } else if (!nextOpen && !isClosedFilter) {
                setIsAllFilter(true); // fallback automatico
            }
        } else if (input === "close") {
            const nextClose = !isClosedFilter;
            setIsClosedFilter(nextClose);

            if (nextClose) {
                setIsOpenFilter(false);
                setIsAllFilter(false);
            } else if (!nextClose && !isOpenFilter) {
                setIsAllFilter(true); // fallback automatico
            }
        } else if (input === "all") {
            setIsAllFilter(true); // può solo essere attivato manualmente
            setIsOpenFilter(false);
            setIsClosedFilter(false);
        }
    }

    // Click report
    function handleReportClick(reportId, event) {
        if (onReportClick) onReportClick(reportId, event);
    }

    return (
        <div className={styles.CommonReportsPage}>
            <h2>{pageTitle}</h2>
            <span>Visualizzati: {itemsString}</span>

            {/* Filtri e search */}
            <div className={styles.ReportsPageLayout}>
                {/* Elenco reports */}
                <div className={styles.filtersSection}>
                    {newReportButtonUrl && (
                        <>
                            <CustomButton
                                disabled={isLoading}
                                onClick={(e) => Redirector.send(newReportButtonUrl, e)}
                            >
                                Crea nuovo Report
                            </CustomButton>
                        </>
                    )}
                    <div className={styles.searchSection}>
                        <FloatingInput
                            type={"text"}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            disabled={isLoading}
                            label={"Cerca Report"}/>
                    </div>
                    <DropdownField label={"Altri filtri"} open={false} children={
                        <div className={styles.filter}>
                            <span>Stato</span>
                            <div className={styles.filterSelections}>
                                <FloatingInput
                                    type={"checkbox"}
                                    disabled={isLoading}
                                    value={isOpenFilter}
                                    onChange={() => switchFilter("open")}
                                    label={"Aperto"}/>
                                <FloatingInput
                                    type={"checkbox"}
                                    disabled={isLoading}
                                    value={isClosedFilter}
                                    onChange={() => switchFilter("close")}
                                    label={"Chiuso"}/>
                                <FloatingInput
                                    type={"checkbox"}
                                    disabled={isLoading || isAllFilter}
                                    value={isAllFilter}
                                    onChange={() => switchFilter("all")}
                                    label={"Tutti"}/>
                            </div>
                        </div>
                    }/>
                </div>
                <div className={styles.reportsContainer}>
                    {filteredReports.map((rep) => (
                        <ReportItem key={rep.id} report={rep} onClick={handleReportClick} adminInfos={adminInfos}/>
                    ))}
                </div>
            </div>

            {/* Paginazione */}
            <div className={styles.paginationColumn}>
                <CustomSelect
                    label="Reports per pagina"
                    defaultValue={itemsPerPage}
                    disabled={isLoading}
                    options={[
                        {label: "Seleziona quanti report", disabled: true},
                        {label: "15"},
                        {label: "30"},
                        {label: "50"}
                    ]}
                    onChange={(val) => {
                        handleItemsPerPageChange(val);
                    }}
                />
            </div>
            <div className={styles.paginationColumn}>
                <div>
                    <label>Pagina: </label>
                    <span>
                        {page + 1}/{maxPage}
                    </span>
                </div>
                <div className={styles.paginationRow}>
                    {page > 2 && (
                        <CustomButton disabled={isLoading} onClick={() => handlePageChange(0)}>
                            &lt;&lt;
                        </CustomButton>
                    )}
                    {page > 0 && (
                        <CustomButton disabled={isLoading} onClick={() => handlePageChange(page - 1)}>
                            &lt;
                        </CustomButton>
                    )}

                    {Array.from({length: 5}, (_, i) => i - 2)
                        .map(offset => page + offset)
                        .filter(p => p >= 0 && p < maxPage)
                        .map(p => (
                            <CustomButton
                                key={p}
                                disabled={isLoading || p === page}
                                className={p === page ? styles.activePage : ""}
                                onClick={() => handlePageChange(p)}
                            >
                                {p + 1}
                            </CustomButton>
                        ))}

                    {page < maxPage - 1 && (
                        <CustomButton disabled={isLoading} onClick={() => handlePageChange(page + 1)}>
                            &gt;
                        </CustomButton>
                    )}
                    {page < maxPage - 3 && (
                        <CustomButton disabled={isLoading} onClick={() => handlePageChange(maxPage - 1)}>
                            &gt;&gt;
                        </CustomButton>
                    )}
                </div>
            </div>
        </div>
    );
}

export default CommonReportsPage;